import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getEnv } from '../utils/env';

const LoginScreen: React.FC = () => {
  const [isLogin, setIsLogin] = useState<boolean>(true);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const { login, register, loginWithToken, error: authError, isLoading, isAuthenticated } = useAuth();
  const [devToken, setDevToken] = useState<string>(() => getEnv('VITE_DEV_ACCESS_TOKEN') || '');
  const isDevMode = import.meta.env.DEV || getEnv('VITE_ENABLE_MOCK_AUTH') === 'true';
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/projects', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);
  // Form-Validierung
  const validateForm = (): boolean => {
    setFormError(null);
    
    if (!email || !password) {
      setFormError('Bitte alle Pflichtfelder ausfüllen');
      return false;
    }
    
    if (!isLogin) {
      if (!name) {
        setFormError('Bitte Namen angeben');
        return false;
      }
      
      if (password !== passwordConfirm) {
        setFormError('Passwörter stimmen nicht überein');
        return false;
      }
      
      if (password.length < 6) {
        setFormError('Passwort muss mindestens 6 Zeichen lang sein');
        return false;
      }
    }
    
    return true;
  };

  // Form-Absenden
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (isLogin) {
      await login(email, password);
    } else {
      await register(name, email, password);
    }
  };

  // Zwischen Login und Registrierung wechseln
  const toggleForm = () => {
    setIsLogin(!isLogin);
    setFormError(null);
  };

  // Google Login über Auth-API (Redirect-Flow)
  const handleGoogleLogin = () => {
    const authApiUrl = getEnv('VITE_AUTH_API_URL');
    const appUrl = window.location.origin;
    
    window.location.href = `${authApiUrl}/google/login?redirect=${encodeURIComponent(appUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            360Volt Dokumentation
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {isLogin ? 'Anmelden um fortzufahren' : 'Neuen Account erstellen'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Fehleranzeige */}
          {(formError || authError) && (
            <div data-testid="login-error" className="bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-200 p-3 rounded">
              {formError || authError}
            </div>
          )}

          {/* Name (nur bei Registrierung) */}
          {!isLogin && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
                className="w-full"
              />
            </div>
          )}

          {/* E-Mail */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-Mail
            </label>
            <input
              id="email"
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@firma.de"
              className="w-full"
              required
            />
          </div>

          {/* Passwort */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Passwort
            </label>
            <input
              id="password"
              data-testid="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full"
              required
            />
          </div>

          {/* Passwort-Bestätigung (nur bei Registrierung) */}
          {!isLogin && (
            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Passwort wiederholen
              </label>
              <input
                id="passwordConfirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          {/* Submit-Button */}
          <button
            type="submit"
            data-testid="login-button"
            className="btn btn-primary w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Bitte warten...
              </span>
            ) : isLogin ? 'Anmelden' : 'Registrieren'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">oder</span>
          </div>
        </div>

        {/* Google Login Button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="flex items-center justify-center gap-3 w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isLogin ? 'Mit Google anmelden' : 'Mit Google registrieren'}
          </button>
        </div>

        {/* Login/Register Toggle */}
        <div className="text-center">
          <button
            type="button"
            onClick={toggleForm}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {isLogin ? 'Neuen Account erstellen' : 'Zurück zur Anmeldung'}
          </button>
        </div>

        {/* Developer Modus */}
        {isDevMode && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dashed border-yellow-400 dark:border-yellow-600"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 font-mono">
                  DEV
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-yellow-700 dark:text-yellow-400">
                Access Token (Bearer)
              </label>
              <textarea
                value={devToken}
                onChange={(e) => setDevToken(e.target.value)}
                placeholder="Token hier einfügen..."
                rows={3}
                className="w-full text-xs font-mono resize-none rounded border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-2 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              />
              <button
                type="button"
                onClick={() => loginWithToken(devToken.trim())}
                disabled={isLoading || !devToken.trim()}
                className="w-full py-2 px-4 rounded border border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-sm hover:bg-yellow-100 dark:hover:bg-yellow-800 disabled:opacity-50 transition-colors"
              >
                Mit Token anmelden
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
